"use client";

import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getBMIColor, getBMIClassification } from "./bmiCalculations";
import useMedicalData from "@/Profile/hooks/useMedicalData";
import { selectEffectiveUserId } from "../../../../sous-profile/redux/selectors/sousProfileSelectors";
import "./BMIChart.css";

const BMI_FILTERS = [
  { key: "ALL", labelKey: "all" },
  { key: "1Y",  labelKey: "oneYear" },
  { key: "6M",  labelKey: "sixMonths" },
  { key: "1M",  labelKey: "oneMonth" },
];

const CLASSIFICATION_KEY = (raw) => {
  const v = String(raw || "").trim().toLowerCase();
  if (v.includes("under"))                      return "underweight";
  if (v.includes("normal"))                     return "normal";
  if (v.includes("over"))                       return "overweight";
  if (v.includes("obes") && v.includes("1"))   return "obesityClass1";
  if (v.includes("obes") && v.includes("2"))   return "obesityClass2";
  if (v.includes("obes"))                       return "obesity";
  return "unknown";
};

const sampleSeries = (labels = [], values = [], maxPoints = 60) => {
  if (!Array.isArray(labels) || !Array.isArray(values)) {
    return { labels: [], values: [] };
  }

  const length = Math.min(labels.length, values.length);
  if (length <= maxPoints) {
    return {
      labels: labels.slice(0, length),
      values: values.slice(0, length),
    };
  }

  const indicesSet = new Set();
  const step = (length - 1) / (maxPoints - 1);

  for (let i = 0; i < maxPoints; i++) {
    indicesSet.add(Math.round(i * step));
  }

  indicesSet.add(0);
  indicesSet.add(length - 1);

  const indices = Array.from(indicesSet).sort((a, b) => a - b);
  return {
    labels: indices.map((index) => labels[index]),
    values: indices.map((index) => values[index]),
  };
};

const BMIChart = ({ userId: propUserId }) => {
  const { t } = useTranslation();

  // ✅ userId: prefer prop (doctor view), fallback to auth (own profile)
  const effectiveUserId = useSelector(selectEffectiveUserId);
  const userId = propUserId ?? effectiveUserId;

  const {
    bmiFromHistory: bmi,
    bmiStatus,
    height,
    RespiratoryRate,
    tempHeight,
    tempWeight,
    setTempHeight,
    setTempWeight,
    handleBmiEditClick:   handleEditClick,
    handleBmiSaveClick:   handleSaveClick,
    handleBmiCancelClick: handleCancelClick,
    saving,
    chartData,
  } = useMedicalData(userId);

  // ─── Active range filter ───────────────────────────────────────
  const [range, setRange] = useState("ALL");

  // ─── Tooltip & hover state ─────────────────────────────────────
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, value: 0, label: "" });
  const [hoveredIndex, setHoveredIndex] = useState(null);

  // ─── isEditing: true when tempHeight/tempWeight are set ────────
  const isEditing = tempHeight !== "" || tempWeight !== "";

  // ─── BMI display ───────────────────────────────────────────────
  const displayBMI        = bmi != null ? bmi.toFixed(1) : "-";
  const rawClassification = bmi != null ? getBMIClassification(bmi) : "-";
  const bmiClassification = rawClassification && rawClassification !== "-"
    ? t(`bmiChart.classification.${CLASSIFICATION_KEY(rawClassification)}`)
    : "-";
  const bmiColor = bmi != null ? getBMIColor(bmi) : "#94a3b8";

  // ─── Chart data for selected range ────────────────────────────
  const currentData = chartData?.[range];
  const rawMonths   = currentData?.labels ?? [];
  const rawValues   = currentData?.values ?? [];

  const { labels: months, values } = useMemo(
    () => sampleSeries(rawMonths, rawValues, 60),
    [rawMonths, rawValues]
  );

  const noData = !currentData || bmi === null;

  // ─── SVG layout constants ──────────────────────────────────────
  const vb = { w: 600, h: 240 };
  const m  = { top: 10, right: 10, bottom: 40, left: 46 };
  const cw = vb.w - m.left - m.right;
  const ch = vb.h - m.top  - m.bottom;
  const yMax = 40, yMin = 15;

  const xFn = (i) =>
    months.length === 1
      ? m.left + cw / 2
      : m.left + (i * cw) / (months.length - 1);

  const yFn = (v) => m.top + ((yMax - v) / (yMax - yMin)) * ch;

  const points = useMemo(
    () => values.map((v, i) => ({ x: xFn(i), y: yFn(v) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values, months.length]
  );

  // ─── Per-segment smooth curves (coloured by BMI value) ────────
  const segments = useMemo(() => {
    if (points.length < 2) return [];
    const segs = [];
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[i - 1] || points[i];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[i + 2] || p2;
      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;
      segs.push({
        d: `M ${p1.x} ${p1.y} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`,
        color: getBMIColor(values[i + 1]),
      });
    }
    return segs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, values]);

  const smoothLineD = useMemo(() => {
    if (points.length < 2) return "";
    let d = `M ${points[0].x} ${points[0].y}`;
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[i - 1] || points[i];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[i + 2] || p2;
      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;
      d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
    }
    return d;
  }, [points]);

  const smoothAreaD = smoothLineD
    ? `${smoothLineD} L ${xFn(values.length - 1)} ${m.top + ch} L ${xFn(0)} ${m.top + ch} Z`
    : "";

  const yTicks = [15, 20, 25, 30, 35, 40];

  // ─── Smart label thinning (avoid overlap when many dates) ───
  const MAX_VISIBLE_LABELS = 10;
  const labelStep = months.length > MAX_VISIBLE_LABELS
    ? Math.ceil(months.length / MAX_VISIBLE_LABELS)
    : 1;

  // ─── Tooltip handlers ──────────────────────────────────────────
  const handlePointMouseEnter = (index, e) => {
    const svgEl       = e.currentTarget.closest("svg");
    const containerEl = e.currentTarget.closest(".bmi-chart");
    if (!svgEl || !containerEl) return;
    const svgRect       = svgEl.getBoundingClientRect();
    const containerRect = containerEl.getBoundingClientRect();
    setHoveredIndex(index);
    setTooltip({
      visible: true,
      x: (points[index].x / vb.w) * svgRect.width  + (svgRect.left - containerRect.left),
      y: (points[index].y / vb.h) * svgRect.height + (svgRect.top  - containerRect.top),
      value: values[index],
      label: months[index],
    });
  };

  const handlePointMouseLeave = () => {
    setHoveredIndex(null);
    setTooltip((p) => ({ ...p, visible: false }));
  };

  // ─── Shared input section (used in both no-data and edit states)
  const InputSection = () => (
    <div className="bmi-input-section">
      <p style={{ marginBottom: 16, color: "#64748b" }}>
        {t("bmiChart.addDataPrompt")}
      </p>
      <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
        <input
          type="number"
          placeholder={t("bmiChart.heightPlaceholder")}
          value={tempHeight}
          onChange={(e) => setTempHeight(e.target.value)}
          style={{ padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 14, width: 120 }}
        />
        <input
          type="number"
          placeholder={t("bmiChart.weightPlaceholder")}
          value={tempWeight}
          onChange={(e) => setTempWeight(e.target.value)}
          style={{ padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 14, width: 120 }}
        />
        <button
          onClick={() => handleSaveClick()}
          disabled={saving}
          style={{ padding: "8px 16px", background: "#006eea ", color: "#fff", border: "none", borderRadius: 6, fontSize: 14, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1 }}
        >
          {saving ? t("bmiChart.saving") : t("bmiChart.save")}
        </button>
        <button
          onClick={handleCancelClick}
          style={{ padding: "8px 16px", background: "#e2e8f0", color: "#333", border: "none", borderRadius: 6, fontSize: 14, cursor: "pointer" }}
        >
          {t("bmiChart.cancel")}
        </button>
      </div>
    </div>
  );

  // ─── NO DATA state ─────────────────────────────────────────────
  if (noData) {
    return (
      <section className="bmi-card">
        <header className="bmi-head">
          <h3>{t("bmiChart.title")}</h3>
        </header>
        <div className="bmi-no-data">
          {isEditing ? (
            <InputSection />
          ) : (
            <div>
              <p>{t("bmiChart.noData")}</p>
              <button
                onClick={handleEditClick}
                style={{ marginTop: 12, padding: "8px 16px", background: "#006eea ", color: "#fff", border: "none", borderRadius: 6, fontSize: 14, cursor: "pointer" }}
              >
                {t("bmiChart.addData")}
              </button>
            </div>
          )}
        </div>
      </section>
    );
  }

  // ─── EMPTY range state ─────────────────────────────────────────
  if (values.length === 0) {
    return (
      <section className="bmi-card">
        <header className="bmi-head">
          <h3>{t("bmiChart.title")}</h3>
          <div className="bmi-filters">
            {BMI_FILTERS.map((f) => (
              <button
                key={f.key}
                className={f.key === range ? "active" : ""}
                onClick={() => setRange(f.key)}
                type="button"
              >
                {t(`bmiChart.filters.${f.labelKey}`)}
              </button>
            ))}
          </div>
        </header>
        <div className="bmi-no-data">
          <p>{t("bmiChart.noDataForPeriod")}</p>
        </div>
      </section>
    );
  }

  // ─── FULL CHART ────────────────────────────────────────────────
  return (
    <section className="bmi-card">
      <header className="bmi-head">
        <div className="bmi-head-title">
          <h3>{t("bmiChart.title")}</h3>
          <div className="bmi-current-info" style={{ color: bmiColor }}>
            <span className="bmi-current-value">
              {t("bmiChart.bmiLabel")}: {displayBMI}
            </span>
            <span className="bmi-current-classification">
              {bmiClassification}
            </span>
          </div>
        </div>

        {/* ✅ Interactive date range filters — switch updates chart data immediately */}
        <div className="bmi-filters">
          {BMI_FILTERS.map((f) => (
            <button
              key={f.key}
              className={f.key === range ? "active" : ""}
              onClick={() => setRange(f.key)}   // ← triggers re-render with new range's data
              type="button"
              aria-label={t(`bmiChart.filters.${f.labelKey}`)}
            >
              {t(`bmiChart.filters.${f.labelKey}`)}
            </button>
          ))}
        </div>
      </header>

      <div className="bmi-chart">
        <svg viewBox={`0 0 ${vb.w} ${vb.h}`} preserveAspectRatio="none">
          <defs>
            <linearGradient id="bmiArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={bmiColor} stopOpacity="0.3" />
              <stop offset="100%" stopColor={bmiColor} stopOpacity="0.06" />
            </linearGradient>
          </defs>

          {yTicks.map((tv) => (
            <g key={`g-${tv}`}>
              <line x1={m.left} x2={m.left + cw} y1={yFn(tv)} y2={yFn(tv)} stroke="#e2e8f0" strokeDasharray="4 4" />
              <text x={m.left - 8} y={yFn(tv) + 4} textAnchor="end" fontSize="10" fill="#94a3b8">{tv}</text>
            </g>
          ))}

          {months.map((mo, i) => {
            // Show only every Nth label; always show first & last
            const isFirst = i === 0;
            const isLast  = i === months.length - 1;
            const isStep  = i % labelStep === 0;
            if (!isFirst && !isLast && !isStep) return null;

            const needsRotation = months.length > MAX_VISIBLE_LABELS;
            return (
              <text
                key={`${mo}-${i}`}
                x={xFn(i)}
                y={m.top + ch + (needsRotation ? 14 : 18)}
                textAnchor={needsRotation ? "end" : "middle"}
                fontSize="10"
                fill="#546881"
                transform={needsRotation ? `rotate(-35, ${xFn(i)}, ${m.top + ch + 14})` : undefined}
              >
                {mo}
              </text>
            );
          })}

          {smoothAreaD && <path d={smoothAreaD} fill="url(#bmiArea)" />}
          {segments.map((seg, i) => (
            <path key={`seg-${i}`} d={seg.d} stroke={seg.color} strokeWidth="2" fill="none" />
          ))}

          {points.map((point, index) => (
            <g key={`point-${index}`}>
              <circle cx={point.x} cy={point.y} r="12" fill="transparent"
                onMouseEnter={(e) => handlePointMouseEnter(index, e)}
                onMouseLeave={handlePointMouseLeave}
                style={{ cursor: "pointer" }}
              />
              <circle cx={point.x} cy={point.y}
                r={hoveredIndex === index ? "7" : "5"}
                fill={getBMIColor(values[index])} stroke="white" strokeWidth="2"
                style={{ pointerEvents: "none" }}
              />
            </g>
          ))}
        </svg>

        {tooltip.visible && (
          <div className="bmi-tooltip" style={{ position: "absolute", left: tooltip.x - 40, top: tooltip.y - 70 }}>
            <div style={{ color: "#6B7280", fontSize: 11 }}>{tooltip.label}</div>
            <div style={{ color: getBMIColor(tooltip.value), fontWeight: 700, fontSize: 16 }}>
              {tooltip.value != null ? Number(tooltip.value).toFixed(1) : "-"}
            </div>
          </div>
        )}
      </div>

      <footer className="bmi-legend">
        <div><span className="dot u"   /> {t("bmiChart.legend.underweight")}</div>
        <div><span className="dot n"   /> {t("bmiChart.legend.normal")}</div>
        <div><span className="dot ow"  /> {t("bmiChart.legend.overweight")}</div>
        <div><span className="dot ob1" /> {t("bmiChart.legend.obesityClass1")}</div>
        <div><span className="dot ob2" /> {t("bmiChart.legend.obesityClass2")}</div>
      </footer>
    </section>
  );
};

export default BMIChart;
